import { Button, Dialog, Input, Typography, IconButton, Checkbox } from "@material-tailwind/react";
import { HiX, HiPlus, HiTag, HiPencil, HiInformationCircle, HiHeart } from "react-icons/hi";
import { useEffect, useState, useRef } from "react";
import ManufacturerAPI from '../../api/manufacturer';
import TradenameAPI from '../../api/tradename';
import { toast } from 'react-toastify';
import { generateUri, validateUri } from '../../utils/uri';
import { useDialog } from '../../context/DialogContext';

const Spinner = ({ size = 5, className = "" }) => (
  <div className={`inline-block size-${size} animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-blue-500 ${className}`}></div>
);

const ManufacturerDialog = ({ isOpen, onClose, onSuccess, mode = "create", manufacturerUri }) => {
  const [loading, setLoading] = useState(false);
  const [manufacturer, setManufacturer] = useState({
    name: '',
    description: '',
    website: '',
    logo_url: '',
    display_name: '',
    uri: '',
    tradenames: [],
    is_sponsor: false
  });
  const [error, setError] = useState({
    uri: '',
    website: '',
    logo_url: ''
  });

  // Use ref to track previous props
  const previousProps = useRef({ isOpen, mode, manufacturerUri });

  // Access the global dialog context
  const { openDialog, setDialogProps, openTradenameDialog } = useDialog();

  // Add these states and functions at the beginning of the component
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (previousProps.current.isOpen !== isOpen ||
      previousProps.current.mode !== mode ||
      (mode === "edit" && previousProps.current.manufacturerUri !== manufacturerUri)) {

      // Update refs
      previousProps.current = { isOpen, mode, manufacturerUri };

      if (isOpen && mode === "edit" && manufacturerUri) {
        fetchManufacturer();
      } else if (isOpen && mode === "create") {
        // Reset form when opening in create mode
        setManufacturer({
          name: '',
          description: '',
          website: '',
          logo_url: '',
          display_name: '',
          uri: '',
          tradenames: [],
          is_sponsor: false
        });
      }
    }
  }, [isOpen, mode, manufacturerUri]);

  const fetchManufacturer = async () => {
    try {
      setLoading(true);

      // Fetch the manufacturer details
      const manufacturerResponse = await ManufacturerAPI.getOne(manufacturerUri);

      // If in edit mode, also fetch the tradenames associated with this manufacturer
      let tradenames = [];
      if (mode === 'edit') {
        try {
          const tradenamesResponse = await ManufacturerAPI.getTradenamesByUri(manufacturerUri);
          if (tradenamesResponse && Array.isArray(tradenamesResponse.tradenames)) {
            tradenames = tradenamesResponse.tradenames.map(tn => ({
              _id: tn._id,
              name: tn.name,
              uri: tn.uri
            }));
          }
        } catch (tradenameError) {
          console.error('Error fetching related tradenames:', tradenameError);
        }
      }

      // Make sure tradenames are properly formatted as objects with _id, name, and uri
      const formattedTradenames = tradenames.length > 0
        ? tradenames
        : Array.isArray(manufacturerResponse.tradenames)
          ? manufacturerResponse.tradenames.map(tn => {
            if (typeof tn === 'object' && tn.name && tn._id && tn.uri) {
              return tn;
            }
            const id = typeof tn === 'object' ? tn._id : tn;
            const name = typeof tn === 'object' ? tn.name : `Tradename ${id}`;
            const uri = typeof tn === 'object' ? tn.uri : '';
            return { _id: id, name, uri };
          })
          : [];

      // Update the manufacturer state with formatted tradenames
      setManufacturer({
        ...manufacturerResponse,
        tradenames: formattedTradenames
      });

      console.log(`Loaded ${formattedTradenames.length} tradenames for manufacturer ${manufacturerResponse.name}`);
    } catch (error) {
      console.error('Error fetching manufacturer:', error);
      toast.error('Failed to fetch manufacturer details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateUri(manufacturer.uri)) {
      handleUriValidation();
      return;
    }

    try {
      setLoading(true);

      // Format the data for API
      const manufacturerData = {
        ...manufacturer,
        tradenames: Array.isArray(manufacturer.tradenames)
          ? manufacturer.tradenames.map(tn => typeof tn === 'object' ? tn._id : tn)
          : []
      };

      let response;
      if (mode === "edit") {
        response = await ManufacturerAPI.edit(manufacturer._id, manufacturerData);
        toast.success('Manufacturer updated successfully');
      } else {
        response = await ManufacturerAPI.create(manufacturerData);
        toast.success('Manufacturer created successfully');
      }

      if (onSuccess) {
        onSuccess(response);
      }

      onClose();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      toast.error(`Failed to ${mode} manufacturer`);
    } finally {
      setLoading(false);
    }
  };

  const handleUriValidation = () => {
    if (!manufacturer.uri) {
      setError(prev => ({ ...prev, uri: 'URI is required' }));
      return false;
    }

    if (!validateUri(manufacturer.uri)) {
      setError(prev => ({ ...prev, uri: 'URI cannot contain whitespace or special characters' }));
      return false;
    }

    setError(prev => ({ ...prev, uri: '' }));
    return true;
  };

  const openTradenameEdit = (uri) => {
    openTradenameDialog({
      mode: 'edit',
      tradenameUri: uri,
      onSuccess: (updatedTradename) => {
        toast.success(`Tradename ${updatedTradename.name} updated`);
      }
    });
  };

  const handleRemoveTradename = (id) => {
    setManufacturer(prev => ({
      ...prev,
      tradenames: prev.tradenames.filter(t => t._id !== id)
    }));
    toast.info('Tradename removed from manufacturer. Save changes to confirm.');
  };

  const handleAddTradename = () => {
    openTradenameDialog({
      mode: 'create',
      initialManufacturers: [{
        _id: manufacturer._id,
        name: manufacturer.name,
        uri: manufacturer.uri
      }],
      onSuccess: (newTradename) => {
        if (newTradename) {
          // Format the new tradename to match our expected structure
          const formattedTradename = {
            _id: newTradename._id,
            name: newTradename.name,
            uri: newTradename.uri
          };

          // Add the new tradename to our list
          setManufacturer(prev => ({
            ...prev,
            tradenames: [...prev.tradenames, formattedTradename]
          }));

          // Show success message
          toast.success(`Tradename ${newTradename.name} added and linked to ${manufacturer.name}`);
        }
      }
    });
  };

  const handleSearchTradenames = async () => {
    if (!searchKeyword) return;

    try {
      setLoading(true);
      const response = await TradenameAPI.search(searchKeyword, 1);
      setSearchResults(response.results || []);
    } catch (error) {
      console.error('Error searching tradenames:', error);
      toast.error('Failed to search tradenames');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingTradename = (tradename) => {
    // Check if this tradename is already in the list
    const exists = manufacturer.tradenames.some(t => t._id === tradename._id);
    if (!exists) {
      setManufacturer(prev => ({
        ...prev,
        tradenames: [...prev.tradenames, {
          _id: tradename._id,
          name: tradename.name,
          uri: tradename.uri
        }]
      }));
      toast.success(`Added ${tradename.name} to this manufacturer`);
    } else {
      toast.info(`${tradename.name} is already added to this manufacturer`);
    }

    // Clear search results and search keyword after adding
    setSearchResults([]);
    setSearchKeyword('');
  };

  const handleAddNewTradename = () => {
    openTradenameDialog({
      mode: 'create',
      initialManufacturers: manufacturer.name ? [{
        name: manufacturer.name,
        uri: manufacturer.uri
      }] : [],
      onSuccess: (newTradename) => {
        if (newTradename) {
          handleAddExistingTradename(newTradename);
        }
      }
    });
  };

  // Add a handler for name changes that generates the URI
  const handleNameChange = (e) => {
    const name = e.target.value;

    setManufacturer(prev => {
      // Only auto-generate URI if in create mode or if URI is empty
      const shouldUpdateUri = mode === "create" || !prev.uri;
      return {
        ...prev,
        name,
        ...(shouldUpdateUri ? { uri: generateUri(name) } : {})
      };
    });
  };

  const validateWebsite = (url) => {
    if (!url) return true; // Empty URL is valid
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleWebsiteChange = (e) => {
    const website = e.target.value;
    setManufacturer(prev => ({ ...prev, website }));

    if (website && !validateWebsite(website)) {
      setError(prev => ({ ...prev, website: 'Please enter a valid URL with protocol (e.g., https://example.com)' }));
    } else {
      setError(prev => ({ ...prev, website: '' }));
    }
  };

  const handleLogoUrlChange = (e) => {
    const logo_url = e.target.value;
    setManufacturer(prev => ({ ...prev, logo_url }));

    if (logo_url && !validateWebsite(logo_url)) {
      setError(prev => ({ ...prev, logo_url: 'Please enter a valid URL with protocol (e.g., https://example.com)' }));
    } else {
      setError(prev => ({ ...prev, logo_url: '' }));
    }
  };

  const handleDisplayNameChange = (e) => {
    const display_name = e.target.value;
    if (display_name.length <= 50) {
      setManufacturer(prev => ({ ...prev, display_name }));
    }
  };

  return (
    <Dialog open={isOpen} handler={onClose} size="lg">
      <Dialog.Overlay>
        <Dialog.Content>
          <div className="p-0">
            <div className="flex items-center justify-between p-4 border-b">
              <Typography variant="h5">
                {mode === "create" ? "Create New Manufacturer" : `Edit ${manufacturer.name || 'Manufacturer'}`}
              </Typography>
              <IconButton onClick={onClose} size="sm">
                <HiX className="w-5 h-5" />
              </IconButton>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="grid gap-4">
                {/* Main Information */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Name *</div>
                    <Input
                      type="text"
                      value={manufacturer.name}
                      onChange={handleNameChange}
                      required
                    />
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">URI *</div>
                    <Input
                      type="text"
                      value={manufacturer.uri}
                      onChange={(e) => setManufacturer({ ...manufacturer, uri: e.target.value })}
                      onBlur={handleUriValidation}
                      error={!!error.uri}
                      required
                      disabled={mode === "edit"}
                    />
                    {error.uri && <p className="mt-1 text-xs text-red-500">{error.uri}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Website</div>
                    <Input
                      type="url"
                      value={manufacturer.website}
                      onChange={handleWebsiteChange}
                      placeholder="https://example.com"
                      error={!!error.website}
                    />
                    {error.website && <p className="mt-1 text-xs text-red-500">{error.website}</p>}
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Logo URL</div>
                    <Input
                      type="url"
                      value={manufacturer.logo_url}
                      onChange={handleLogoUrlChange}
                      placeholder="https://example.com/logo.png"
                      error={!!error.logo_url}
                    />
                    {error.logo_url && <p className="mt-1 text-xs text-red-500">{error.logo_url}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Display Name</div>
                    <Input
                      type="text"
                      value={manufacturer.display_name}
                      onChange={handleDisplayNameChange}
                      placeholder="Enter display name (max 50 characters)"
                      maxLength={50}
                    />
                    <div className="mt-1 text-xs text-gray-500 text-right">
                      {manufacturer.display_name?.length || 0}/50
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 font-medium text-gray-700">Sponsor/ Top Pharmacy</div>

                  <div className="flex items-center justify-start gap-4">
                    <div className="flex items-center pt-4">
                      <Checkbox defaultChecked={manufacturer.is_sponsor} id="is_sponsor" onChange={(e) => setManufacturer(prev => ({ ...prev, is_sponsor: e.target.checked }))}>
                        <Checkbox.Indicator />
                      </Checkbox>
                      <Typography
                        as="label"
                        htmlFor="is_sponsor"
                        className="cursor-pointer text-foreground font-medium text-gray-700 ml-2"
                      >
                        Is Sponsor
                      </Typography>
                    </div>

                    <div className="flex items-center pt-4">
                      <Checkbox defaultChecked={manufacturer.is_top_manufacturer} id="is_top_manufacturer" onChange={(e) => setManufacturer(prev => ({ ...prev, is_top_manufacturer: e.target.checked }))}>
                        <Checkbox.Indicator />
                      </Checkbox>
                      <Typography
                        as="label"
                        htmlFor="is_top_manufacturer"
                        className="cursor-pointer text-foreground font-medium text-gray-700 ml-2"
                      >
                        Is Top Manufacturer
                      </Typography>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Add Tradename section for create mode */}
                {mode === 'create' && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Typography className="font-semibold">
                        Add Tradenames
                      </Typography>
                    </div>
                    <div className="p-3 rounded-md bg-gray-50">
                      <div className="mb-3">
                        <div className="mb-2 font-medium text-gray-700">Search Existing Tradenames</div>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Search by name"
                            value={searchKeyword || ''}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            onClick={handleSearchTradenames}
                            className="px-4"
                          >
                            Search
                          </Button>
                        </div>
                      </div>

                      {searchResults.length > 0 && (
                        <div className="mb-3">
                          <div className="mb-2 font-medium text-gray-700">Search Results</div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {searchResults.map(tradename => (
                              <div
                                key={tradename._id}
                                className="flex items-center justify-between p-2 mb-2 bg-white border border-gray-200 rounded hover:bg-blue-50"
                              >
                                <div>{tradename.name}</div>
                                <Button
                                  size="sm"
                                  onClick={() => handleAddExistingTradename(tradename)}
                                >
                                  Add
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Preview of added tradenames */}
                      {manufacturer.tradenames.length > 0 && (
                        <div className="mb-3">
                          <div className="mb-2 font-medium text-gray-700">Added Tradenames ({manufacturer.tradenames.length})</div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {manufacturer.tradenames.map((tradename) => (
                              <div
                                key={tradename._id}
                                className="flex items-center justify-between p-2 mb-2 bg-white border border-gray-200 rounded"
                              >
                                <div className="flex items-center">
                                  <HiTag className="mr-2 text-blue-500" />
                                  <span>{tradename.name}</span>
                                </div>
                                <IconButton
                                  size="sm"
                                  onClick={() => handleRemoveTradename(tradename._id)}
                                  className="p-1"
                                >
                                  <HiX className="text-red-500" />
                                </IconButton>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-center">
                        <Button
                          onClick={handleAddNewTradename}
                          className="flex items-center gap-2"
                        >
                          <HiPlus className="w-4 h-4" /> Add New Tradename
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tradenames Section */}
                {mode === 'edit' && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Typography variant="subtitle1" className="font-semibold">
                        Tradenames {manufacturer.tradenames && manufacturer.tradenames.length > 0 && `(${manufacturer.tradenames.length})`}
                      </Typography>
                      <Button
                        size="sm"
                        type="button"
                        onClick={handleAddTradename}
                        className="flex items-center gap-2"
                      >
                        <HiPlus className="w-3 h-3" /> Add Tradename
                      </Button>
                    </div>

                    <div className="p-3 rounded-md bg-gray-50">
                      {manufacturer.tradenames && manufacturer.tradenames.length > 0 ? (
                        <div className="grid gap-2">
                          {manufacturer.tradenames.map((tradename) => (
                            <div
                              key={tradename._id}
                              className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded hover:bg-blue-50"
                            >
                              <div className="flex items-center">
                                <HiTag className="mr-2 text-blue-500" />
                                <span>{tradename.name}</span>
                              </div>
                              <div className="flex gap-2">
                                <IconButton
                                  size="sm"
                                  onClick={() => openTradenameEdit(tradename.uri)}
                                  className="p-1"
                                >
                                  <HiPencil className="text-blue-500" />
                                </IconButton>
                                <IconButton
                                  size="sm"
                                  onClick={() => handleRemoveTradename(tradename._id)}
                                  className="p-1"
                                >
                                  <HiX className="text-red-500" />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-4 text-center text-gray-500">
                          {loading ? (
                            <div className="flex justify-center">
                              <Spinner size={6} />
                            </div>
                          ) : (
                            <>
                              <HiInformationCircle className="mx-auto mb-2 text-xl text-blue-400" />
                              <p>No tradenames associated with this manufacturer yet.</p>
                              <p className="mt-1 text-sm">Click "Add Tradename" to associate a tradename with this manufacturer.</p>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <Typography variant="caption" className="block mt-1 text-gray-500">
                      Note: Changes to tradename associations will be saved when you click "Save Changes".
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-4 p-4 border-t">
              <Button
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || (mode === "create" && (!manufacturer.name || !manufacturer.uri || Boolean(error.uri)))}
              >
                {loading ?
                  <span className="flex items-center gap-2">
                    <Spinner size={4} />
                    Saving...
                  </span> :
                  mode === "create" ? "Create Manufacturer" : "Save Changes"
                }
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
};

export default ManufacturerDialog; 